import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { useForm } from "@tanstack/react-form";
import { ApiInternal } from "src/api/api_internal";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import {
    ArrowTopRightOnSquareIcon,
    EllipsisVerticalIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import Form from "src/components/base/form";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { EmptyState } from "src/components/base/empty-state";
import { RectangleStackIcon } from "@heroicons/react/24/solid";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import TablePagination from "src/components/table-pagination";
import { UUID } from "src/api/api_common";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { Button, PrimaryButton } from "src/components/base/button";
import { FullRuleset } from "src/api/generated/frontend_internal_v1";
import DeleteRulesetDialog from "src/components/dialogs/delete-ruleset";
import CreateRulesetDialog from "src/components/dialogs/create-ruleset";
import UpdateRulesetDialog from "src/components/dialogs/update-ruleset";

/**
 * The properties for {@link RulesetsOverview}
 */
export type RulesetsOverviewProps = {};

const LIMIT = 25;

/**
 * Overview page for rulesets
 */
export default function RulesetsOverview(props: RulesetsOverviewProps) {
    const [t] = useTranslation("admin-rulesets");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const [openCreateRuleset, setopenCreateRuleset] = React.useState(false);
    const [openUpdateRuleset, setOpenUpdateRuleset] = React.useState<FullRuleset | undefined>(undefined);
    const [openDeleteRuleset, setOpenDeleteRuleset] = React.useState<FullRuleset | undefined>(undefined);

    const { search, source, page } = Route.useSearch();
    const data = Route.useLoaderData();

    const showPluginColumn = source !== "Local";

    const form = useForm({
        defaultValues: {
            search: search,
            source: source,
        },
    });

    return (
        <HeadingLayout
            heading={t("heading.overview-rulesets")}
            headingChildren={
                <PrimaryButton onClick={() => setopenCreateRuleset(true)}>{t("button.create-ruleset")}</PrimaryButton>
            }
        >
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/a/rulesets",
                                        search: {
                                            page: 1,
                                            source,
                                            search: fieldApi.state.value,
                                        },
                                    });
                                },
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        placeholder={t("label.name")}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"source"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/a/rulesets",
                                        search: {
                                            page: 1,
                                            search,
                                            source: fieldApi.state.value,
                                        },
                                    });
                                },
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.source")}</Label>
                                    <Listbox
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e as RulesetSourceFilter)}
                                    >
                                        <ListboxOption value={""}>
                                            <ListboxLabel>{t("label.show-all")}</ListboxLabel>
                                        </ListboxOption>
                                        <ListboxOption value={"Local"}>
                                            <ListboxLabel>{t("label.show-local")}</ListboxLabel>
                                        </ListboxOption>
                                        <ListboxOption value={"AnyPlugin"}>
                                            <ListboxLabel>{t("label.show-remote")}</ListboxLabel>
                                        </ListboxOption>
                                    </Listbox>
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>
            {data.items.length === 0 ? (
                <EmptyState
                    icon={<RectangleStackIcon />}
                    title={tg("empty.no-results-title")}
                    description={tg("empty.no-results-description")}
                />
            ) : (
                <Table stickyHeader={true}>
                    <TableHead>
                        <TableRow>
                            {showPluginColumn && <TableHeader>{t("label.plugin")}</TableHeader>}
                            <TableHeader>{t("label.name")}</TableHeader>
                            <TableHeader>{t("label.description")}</TableHeader>
                            <TableHeader>{t("label.rule-count")}</TableHeader>
                            <TableHeader className={"w-0"}>
                                <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                            </TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.items.map((ruleset) => (
                            <TableRow
                                key={ruleset.uuid}
                                href={"/a/rulesets/$rulesetId/rules"}
                                params={{ rulesetId: ruleset.uuid }}
                            >
                                {showPluginColumn && (
                                    <TableCell>
                                        {ruleset.plugin && (
                                            <Button
                                                plain
                                                href={"/a/plugins/$pluginId"}
                                                params={{ pluginId: ruleset.plugin.uuid }}
                                            >
                                                {ruleset.plugin.name}
                                                <ArrowTopRightOnSquareIcon />
                                            </Button>
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>{ruleset.name}</TableCell>
                                <TableCell className={"w-[50ch] text-wrap break-all whitespace-pre-wrap"}>
                                    {ruleset.description}
                                </TableCell>
                                <TableCell>{ruleset.rules.length}</TableCell>
                                <TableCell>
                                    {!ruleset.plugin && (
                                        <Dropdown>
                                            <DropdownButton plain={true}>
                                                <EllipsisVerticalIcon />
                                            </DropdownButton>
                                            <DropdownMenu>
                                                <DropdownSection>
                                                    <DropdownItem onClick={() => setOpenUpdateRuleset(ruleset)}>
                                                        <PencilSquareIcon />
                                                        {t("button.edit-ruleset")}
                                                    </DropdownItem>
                                                </DropdownSection>
                                                <DropdownSection>
                                                    <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                                    <DropdownItem onClick={() => setOpenDeleteRuleset(ruleset)}>
                                                        <TrashIcon />
                                                        <DropdownLabel>{t("button.delete-ruleset")}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownSection>
                                            </DropdownMenu>
                                        </Dropdown>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/rulesets"}
                getSearchParams={(newPage: number) => ({ search, page: newPage })}
            />

            <Suspense>
                <CreateRulesetDialog
                    isOpen={openCreateRuleset}
                    onSubmit={async () => {
                        setopenCreateRuleset(false);
                        await router.invalidate({ sync: true });
                    }}
                    onClose={() => setopenCreateRuleset(false)}
                />

                <UpdateRulesetDialog
                    ruleset={openUpdateRuleset}
                    isOpen={!!openUpdateRuleset}
                    onSubmit={async () => {
                        setOpenUpdateRuleset(undefined);
                        await router.invalidate({ sync: true });
                    }}
                    onClose={() => setOpenUpdateRuleset(undefined)}
                />

                <DeleteRulesetDialog
                    isOpen={!!openDeleteRuleset}
                    onClose={() => setOpenDeleteRuleset(undefined)}
                    onDelete={async () => {
                        setOpenDeleteRuleset(undefined);
                        await router.invalidate({ sync: true });
                    }}
                    ruleset={openDeleteRuleset}
                />
            </Suspense>
        </HeadingLayout>
    );
}

/** The search parameters for the rulesets overview */
export interface RulesetsOverviewSearch {
    /** The current page we're on */
    page: number;
    /** The search term we're searching for */
    search: string;
    /** The source of the ruleset*/
    source: RulesetSourceFilter;
}

/** Filter by the source of the rulesets */
type RulesetSourceFilter = "" | "Local" | "AnyPlugin" | UUID;

export const Route = createFileRoute("/_admin/a/rulesets/")({
    component: RulesetsOverview,
    validateSearch: (search: Record<string, unknown>): RulesetsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
            source: (search.source as RulesetSourceFilter) || "",
        };
    },
    loaderDeps: ({ search }) => search,
    loader: async ({ deps }) =>
        await ApiInternal.rulesets.getPage({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
            filter_source: deps.source === "" ? undefined : deps.source,
        }),
});
